import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Reply, SendHorizontal, Pencil, Check, X } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useTranslation } from "@/i18n/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { useLocation } from "wouter";

interface User {
  id: number;
  email: string;
  displayName?: string;
  avatarUrl?: string;
}

interface Message {
  id: number;
  content: string;
  createdAt: string;
  updatedAt: string;
  userId: number;
  user: User;
  replies: Reply[];
}

interface Reply {
  id: number;
  content: string;
  createdAt: string;
  updatedAt: string;
  userId: number;
  user: User;
}

interface PuzzleChatProps {
  puzzleId: number;
  currentUser?: User | null;
}

export default function PuzzleChat({ puzzleId, currentUser }: PuzzleChatProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const [newMessage, setNewMessage] = useState("");
  const [replyContent, setReplyContent] = useState("");
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [editingReplyId, setEditingReplyId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState("");

  // Fetch messages
  const { data: messages, isLoading } = useQuery<Message[]>({
    queryKey: [`/api/puzzles/custom/${puzzleId}/messages`],
    refetchInterval: 10000, // Refetch every 10 seconds to get new messages
  });

  // Create a new message
  const createMessageMutation = useMutation({
    mutationFn: async (content: string) => {
      const response = await fetch(`/api/puzzles/custom/${puzzleId}/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ content }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create message");
      }
      
      return response.json();
    },
    onSuccess: () => {
      setNewMessage("");
      queryClient.invalidateQueries({ queryKey: [`/api/puzzles/custom/${puzzleId}/messages`] });
      toast({
        title: t("puzzle_chat.message_posted", { fallback: "Message posted" }),
        description: t("puzzle_chat.message_posted_description", { fallback: "Your message has been posted successfully" }),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("puzzle_chat.error", { fallback: "Error" }),
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // Edit a message
  const editMessageMutation = useMutation({
    mutationFn: async ({ messageId, content }: { messageId: number, content: string }) => {
      const response = await fetch(`/api/puzzles/custom/${puzzleId}/messages/${messageId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ content }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update message");
      }
      
      return response.json();
    },
    onSuccess: () => {
      setEditingMessageId(null);
      setEditContent("");
      queryClient.invalidateQueries({ queryKey: [`/api/puzzles/custom/${puzzleId}/messages`] });
      toast({
        title: t("puzzle_chat.message_updated", { fallback: "Message updated" }),
        description: t("puzzle_chat.message_updated_description", { fallback: "Your message has been updated successfully" }),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("puzzle_chat.error", { fallback: "Error" }),
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // Create a reply to a message
  const createReplyMutation = useMutation({
    mutationFn: async ({ messageId, content }: { messageId: number, content: string }) => {
      const response = await fetch(`/api/puzzles/custom/${puzzleId}/messages/${messageId}/replies`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ content }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create reply");
      }
      
      return response.json();
    },
    onSuccess: () => {
      setReplyContent("");
      setReplyingTo(null);
      queryClient.invalidateQueries({ queryKey: [`/api/puzzles/custom/${puzzleId}/messages`] });
      toast({
        title: t("puzzle_chat.reply_posted", { fallback: "Reply posted" }),
        description: t("puzzle_chat.reply_posted_description", { fallback: "Your reply has been posted successfully" }),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("puzzle_chat.error", { fallback: "Error" }),
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // Edit a reply
  const editReplyMutation = useMutation({
    mutationFn: async ({ messageId, replyId, content }: { messageId: number, replyId: number, content: string }) => {
      const response = await fetch(`/api/puzzles/custom/${puzzleId}/messages/${messageId}/replies/${replyId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ content }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update reply");
      }
      
      return response.json();
    },
    onSuccess: () => {
      setEditingReplyId(null);
      setEditContent("");
      queryClient.invalidateQueries({ queryKey: [`/api/puzzles/custom/${puzzleId}/messages`] });
      toast({
        title: t("puzzle_chat.reply_updated", { fallback: "Reply updated" }),
        description: t("puzzle_chat.reply_updated_description", { fallback: "Your reply has been updated successfully" }),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("puzzle_chat.error", { fallback: "Error" }),
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const handleSubmitMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) {
      toast({
        title: t("puzzle_chat.login_required", { fallback: "Login required" }),
        description: t("puzzle_chat.login_required_description", { fallback: "You need to be logged in to post messages" }),
        variant: "destructive",
      });
      setLocation("/auth");
      return;
    }

    if (!newMessage.trim()) {
      toast({
        title: t("puzzle_chat.empty_message", { fallback: "Empty message" }),
        description: t("puzzle_chat.empty_message_description", { fallback: "Please enter a message" }),
        variant: "destructive",
      });
      return;
    }

    createMessageMutation.mutate(newMessage);
  };

  const handleSubmitReply = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) {
      toast({
        title: t("puzzle_chat.login_required", { fallback: "Login required" }),
        description: t("puzzle_chat.login_required_description", { fallback: "You need to be logged in to post replies" }),
        variant: "destructive",
      });
      setLocation("/auth");
      return;
    }

    if (!replyContent.trim() || replyingTo === null) {
      toast({
        title: t("puzzle_chat.empty_reply", { fallback: "Empty reply" }),
        description: t("puzzle_chat.empty_reply_description", { fallback: "Please enter a reply" }),
        variant: "destructive",
      });
      return;
    }

    createReplyMutation.mutate({ messageId: replyingTo, content: replyContent });
  };

  const handleEditMessage = (message: Message) => {
    setEditingMessageId(message.id);
    setEditContent(message.content);
    setEditingReplyId(null);
    setReplyingTo(null);
  };

  const handleSaveEditedMessage = () => {
    if (!editContent.trim() || editingMessageId === null) {
      toast({
        title: t("puzzle_chat.empty_message", { fallback: "Empty message" }),
        description: t("puzzle_chat.empty_message_description", { fallback: "Please enter a message" }),
        variant: "destructive",
      });
      return;
    }

    editMessageMutation.mutate({ messageId: editingMessageId, content: editContent });
  };

  const handleEditReply = (messageId: number, reply: Reply) => {
    setEditingReplyId(reply.id);
    setEditContent(reply.content);
    setEditingMessageId(null);
    setReplyingTo(null);
  };

  const handleSaveEditedReply = (messageId: number) => {
    if (!editContent.trim() || editingReplyId === null) {
      toast({
        title: t("puzzle_chat.empty_reply", { fallback: "Empty reply" }),
        description: t("puzzle_chat.empty_reply_description", { fallback: "Please enter a reply" }),
        variant: "destructive",
      });
      return;
    }

    editReplyMutation.mutate({ 
      messageId, 
      replyId: editingReplyId, 
      content: editContent 
    });
  };

  const formatCreatedAt = (createdAt: string) => {
    try {
      return formatDistanceToNow(new Date(createdAt), { addSuffix: true });
    } catch (error) {
      console.error("Date formatting error:", error);
      return createdAt;
    }
  };

  const getUserInitials = (user: User) => {
    if (user.displayName) {
      return user.displayName.charAt(0).toUpperCase();
    }
    return user.email.charAt(0).toUpperCase();
  };

  const isEdited = (createdAt: string, updatedAt: string) => {
    // Check if createdAt and updatedAt are different
    return new Date(createdAt).getTime() !== new Date(updatedAt).getTime();
  };

  const cancelEditing = () => {
    setEditingMessageId(null);
    setEditingReplyId(null);
    setEditContent("");
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-white mb-2">
        {t("puzzle_chat.title", { fallback: "Discussion" })}
      </h3>

      {/* New message form */}
      <form onSubmit={handleSubmitMessage} className="space-y-2">
        <Textarea
          placeholder={t("puzzle_chat.new_message_placeholder", { fallback: "Type your message here..." })}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          className="bg-zinc-800 border-zinc-700 text-zinc-300"
        />
        <div className="flex justify-end">
          <Button 
            type="submit" 
            disabled={createMessageMutation.isPending}
            className="flex items-center"
          >
            {createMessageMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <SendHorizontal className="mr-2 h-4 w-4" />
            )}
            {t("puzzle_chat.post", { fallback: "Post" })}
          </Button>
        </div>
      </form>

      {/* Messages list */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="text-center py-4">
            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
          </div>
        ) : messages && messages.length > 0 ? (
          messages.map((message) => (
            <div key={message.id} className="bg-zinc-800 rounded-md p-3 sm:p-4 space-y-3 sm:space-y-4 max-w-full">
              <div className="flex items-start gap-2 sm:gap-3">
                <Avatar className="h-8 w-8">
                  {message.user.avatarUrl ? (
                    <AvatarImage src={message.user.avatarUrl} alt={message.user.displayName || message.user.email} />
                  ) : (
                    <AvatarFallback className="bg-blue-600">{getUserInitials(message.user)}</AvatarFallback>
                  )}
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap justify-between items-center mb-1 gap-1">
                    <p className="text-sm font-medium text-zinc-300 break-words">
                      <a 
                        href={`/users/${message.user.displayName ? message.user.displayName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : message.user.id}`}
                        className="hover:underline hover:text-blue-400 transition-colors"
                        onClick={(e) => {
                          e.preventDefault();
                          setLocation(`/users/${message.user.displayName ? message.user.displayName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : message.user.id}`);
                        }}
                      >
                        {message.user.displayName || message.user.email.split('@')[0]}
                      </a>
                    </p>
                    <span className="text-xs text-zinc-500 flex items-center gap-1 flex-shrink-0">
                      {formatCreatedAt(message.createdAt)}
                      {isEdited(message.createdAt, message.updatedAt) && (
                        <span className="italic text-zinc-500">{t("puzzle_chat.edited", { fallback: "(edited)" })}</span>
                      )}
                    </span>
                  </div>
                  
                  {editingMessageId === message.id ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="bg-zinc-900 border-zinc-700 text-zinc-300 text-sm"
                      />
                      <div className="flex justify-end space-x-2">
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          onClick={cancelEditing}
                          className="h-7 text-xs"
                        >
                          <X className="mr-1 h-3 w-3" />
                          {t("cancel", { fallback: "Cancel" })}
                        </Button>
                        <Button 
                          type="button" 
                          size="sm"
                          onClick={handleSaveEditedMessage}
                          disabled={editMessageMutation.isPending}
                          className="h-7 text-xs"
                        >
                          {editMessageMutation.isPending ? (
                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                          ) : (
                            <Check className="mr-1 h-3 w-3" />
                          )}
                          {t("puzzle_chat.save", { fallback: "Save" })}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="max-w-full overflow-hidden">
                      <p className="text-zinc-300 whitespace-pre-line" style={{ wordWrap: 'break-word', wordBreak: 'break-all', overflowWrap: 'break-word', hyphens: 'auto' }}>
                        {message.content.replace(/\S{30,}/g, (match) => {
                          // Insert zero-width spaces every 10 characters in very long words
                          return match.replace(/(.{10})/g, '$1\u200B');
                        })}
                      </p>
                    </div>
                  )}
                  
                  <div className="flex gap-2 mt-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-7 text-xs text-zinc-500"
                      onClick={() => setReplyingTo(message.id)}
                    >
                      <Reply className="mr-1 h-3 w-3" />
                      {t("puzzle_chat.reply", { fallback: "Reply" })}
                    </Button>
                    
                    {currentUser && currentUser.id === message.userId && editingMessageId !== message.id && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 text-xs text-zinc-500"
                        onClick={() => handleEditMessage(message)}
                      >
                        <Pencil className="mr-1 h-3 w-3" />
                        {t("puzzle_chat.edit", { fallback: "Edit" })}
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Replies list */}
              {message.replies && message.replies.length > 0 && (
                <div className="pl-4 sm:pl-10 space-y-3 border-l border-zinc-700">
                  {message.replies.map((reply) => (
                    <div key={reply.id} className="flex items-start gap-3">
                      <Avatar className="h-6 w-6">
                        {reply.user.avatarUrl ? (
                          <AvatarImage src={reply.user.avatarUrl} alt={reply.user.displayName || reply.user.email} />
                        ) : (
                          <AvatarFallback className="bg-blue-600 text-xs">{getUserInitials(reply.user)}</AvatarFallback>
                        )}
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap justify-between items-center mb-1 gap-1">
                          <p className="text-xs font-medium text-zinc-300 break-words">
                            <a 
                              href={`/users/${reply.user.displayName ? reply.user.displayName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : reply.user.id}`}
                              className="hover:underline hover:text-blue-400 transition-colors"
                              onClick={(e) => {
                                e.preventDefault();
                                setLocation(`/users/${reply.user.displayName ? reply.user.displayName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : reply.user.id}`);
                              }}
                            >
                              {reply.user.displayName || reply.user.email.split('@')[0]}
                            </a>
                          </p>
                          <span className="text-xs text-zinc-500 flex items-center gap-1 flex-shrink-0">
                            {formatCreatedAt(reply.createdAt)}
                            {isEdited(reply.createdAt, reply.updatedAt) && (
                              <span className="italic text-zinc-500">{t("puzzle_chat.edited", { fallback: "(edited)" })}</span>
                            )}
                          </span>
                        </div>
                        
                        {editingReplyId === reply.id ? (
                          <div className="space-y-2">
                            <Textarea
                              value={editContent}
                              onChange={(e) => setEditContent(e.target.value)}
                              className="bg-zinc-900 border-zinc-700 text-zinc-300 text-sm"
                            />
                            <div className="flex justify-end space-x-2">
                              <Button 
                                type="button" 
                                variant="ghost" 
                                size="sm"
                                onClick={cancelEditing}
                                className="h-7 text-xs"
                              >
                                <X className="mr-1 h-3 w-3" />
                                {t("cancel", { fallback: "Cancel" })}
                              </Button>
                              <Button 
                                type="button" 
                                size="sm"
                                onClick={() => handleSaveEditedReply(message.id)}
                                disabled={editReplyMutation.isPending}
                                className="h-7 text-xs"
                              >
                                {editReplyMutation.isPending ? (
                                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                ) : (
                                  <Check className="mr-1 h-3 w-3" />
                                )}
                                {t("puzzle_chat.save", { fallback: "Save" })}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="max-w-full overflow-hidden">
                            <p className="text-sm text-zinc-300 whitespace-pre-line" style={{ wordWrap: 'break-word', wordBreak: 'break-all', overflowWrap: 'break-word', hyphens: 'auto' }}>
                              {reply.content.replace(/\S{30,}/g, (match) => {
                                // Insert zero-width spaces every 10 characters in very long words
                                return match.replace(/(.{10})/g, '$1\u200B');
                              })}
                            </p>
                          </div>
                        )}
                        
                        {currentUser && currentUser.id === reply.userId && editingReplyId !== reply.id && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="mt-1 h-6 text-xs text-zinc-500"
                            onClick={() => handleEditReply(message.id, reply)}
                          >
                            <Pencil className="mr-1 h-3 w-3" />
                            {t("puzzle_chat.edit", { fallback: "Edit" })}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Reply form */}
              {replyingTo === message.id && (
                <form onSubmit={handleSubmitReply} className="pl-4 sm:pl-10 pt-2 border-l border-zinc-700">
                  <div className="space-y-2">
                    <Input
                      placeholder={t("puzzle_chat.reply_placeholder", { fallback: "Type your reply..." })}
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      className="bg-zinc-900 border-zinc-700 text-zinc-300"
                    />
                    <div className="flex gap-2 justify-end">
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setReplyingTo(null);
                          setReplyContent("");
                        }}
                      >
                        {t("cancel", { fallback: "Cancel" })}
                      </Button>
                      <Button 
                        type="submit" 
                        size="sm"
                        disabled={createReplyMutation.isPending}
                      >
                        {createReplyMutation.isPending ? (
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        ) : (
                          <SendHorizontal className="mr-1 h-3 w-3" />
                        )}
                        {t("puzzle_chat.post_reply", { fallback: "Reply" })}
                      </Button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          ))
        ) : (
          <p className="text-zinc-400 italic text-center py-4">
            {t("puzzle_chat.no_messages", { fallback: "No messages yet. Start the discussion!" })}
          </p>
        )}
      </div>
    </div>
  );
}
